import { useNavigation, useTranslate } from '@refinedev/core';
import { Button, Tooltip } from 'antd';
import { useSubscriptionWarnings } from '../../hooks';
import { palette } from '../../styles/serene-shell.theme';

type Props = {
  hideUpgradeButton?: boolean;
};

/** Serene Shell's error container — the tint behind a limit that is reached. */
const ERROR_CONTAINER = '#FFDAD6';

/**
 * The practice's client seats — "43 / 50" — shown once the practice is near its limit (80 %) or at
 * it. A pill the height of the header's buttons (44 px), so it sits on their line instead of
 * towering over it; the long "Clients Added / Subscription Clients" wording is the tooltip. It wraps
 * as one unit on a narrow screen (founder, 2026-09-23: "not visually pleasing / well aligned … do
 * not make it too ugly on smaller screens"). No fill bar — the founder, the same day: "just eating
 * up space and makes text visibility worse without adding value"; the number and the tint say it.
 */
export const ClientsLimitReached = ({ hideUpgradeButton = false }: Props) => {
  const subWarnings = useSubscriptionWarnings();

  const translate = useTranslate();

  const { replace } = useNavigation();

  if (!subWarnings.limitReached && !subWarnings.limitApproaching) {
    return null;
  }

  const reached = subWarnings.limitReached;
  const ink = reached ? palette.error : palette.secondary;

  return (
    <div
      data-testid="cc-clients-limit-reached"
      data-level={reached ? 'reached' : 'approaching'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        maxWidth: '100%',
      }}
    >
      <Tooltip title={translate('invites.clientsStats')}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 44,
            padding: '0 18px',
            borderRadius: 999,
            border: `1px solid ${
              reached ? palette.error : palette.secondaryContainer
            }`,
            background: reached ? ERROR_CONTAINER : palette.secondaryFixed,
            color: ink,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {translate('clients.clients')}
          </span>
          <span
            data-testid="cc-clients-limit-value"
            style={{
              fontSize: 17,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.01em',
            }}
          >
            {subWarnings.clientsAdded} / {subWarnings.clientsPurchased}
          </span>
        </div>
      </Tooltip>

      {!hideUpgradeButton && (
        <Button
          type="link"
          onClick={() => replace('/portalSubscriptions')}
          style={{ fontWeight: 600 }}
        >
          {translate('invites.upgrade')}
        </Button>
      )}
    </div>
  );
};
